import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FirstPage, LastPage } from "@mui/icons-material";
import dayjs from "dayjs";
import { storageLeadList } from "../storageStaticData";
import CustomButton from "../../../../components/common/button/CustomButton";
import ManageStorage from "./ManageStorage";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllStorageAction,
  getSingleStorageAction,
  setStorageResponse,
} from "../../../../store/reducers/storageSlice";
import {
  setAddNewLeadAction,
  setUpdateLeadAction,
} from "../../../../store/reducers/leadSlice";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import ManageStorageFilter from "./ManageStorageFilter";

/* ─── color helper for storage statuses ─── */
const getColor = (status: string): string => {
  switch (status) {
    case "STORAGE_ENQUIRY":
      return "#5a3286";
    case "STORAGE":
      return "#274e13";
    case "COMPLETE":
      return "#000000";
    default:
      return "#5a3286";
  }
};

/* ─── Single card component ─── */
const StorageLeadListCard: React.FC<{
  lead: any;
  selectedLead: string | null;
  onSelect: (id: string) => void;
}> = ({ lead, selectedLead, onSelect }) => {
  const dateData = [
    {
      label: "F",
      fullLabel: "Follow Up Date",
      date: lead?.follow_up_date,
      color: "#FF4C4C",
    },
    {
      label: "A",
      fullLabel: "Created At",
      date: lead?.created_at,
      color: "#339DFF",
    },
    {
      label: "M",
      fullLabel: "Moving On Date",
      date: lead?.moving_on_date,
      color: "#2DBE60",
    },
    {
      label: "L",
      fullLabel: "Last Updated",
      date: lead?.updated_at,
      color: "#FF934F",
    },
  ];

  const formatShortDate = (dateString: string) => {
    if (!dateString) return "--";
    return dayjs(dateString).format("DD\nMMM").toUpperCase();
  };

  const formatFullDate = (dateString: string) => {
    if (!dateString) return "--";
    return dayjs(dateString).format("DD MMMM YYYY");
  };

  return (
    <Paper
      elevation={1}
      sx={{
        border: `1px solid ${
          selectedLead === lead.leadId ? getColor(lead?.stage) : "#ddd"
        }`,
        display: "flex",
        alignItems: "center",
        p: "2px",
        backgroundColor:
          selectedLead === lead?.leadId
            ? alpha(getColor(lead?.stage), 0.15)
            : "#fff",
        position: "relative",
        overflow: "hidden",
        marginBottom: "4px",
        maxHeight: "100vh",
        cursor: "pointer",
      }}
      onClick={() => onSelect(lead?.leadId)}
    >
      {/* Left Accent */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 10,
          backgroundColor: getColor(lead?.stage),
        }}
      />

      {/* Content */}
      <Box sx={{ ml: 2, width: "100%" }}>
        <Grid container spacing={0}>
          <Grid item xs={5.5}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ lineHeight: 1.5, textTransform: "capitalize" }}
            >
              {lead?.customerPrefix ? `${lead.customerPrefix} - ` : ""}
              {lead?.customerName}
            </Typography>
          </Grid>

          {dateData.map((item, index) => (
            <Grid
              key={index}
              item
              xs
              sx={{
                padding: "2px 4px",
                textAlign: "center",
                display: "grid",
              }}
            >
              <Tooltip
                title={`${item.fullLabel} - ${formatFullDate(item.date)}`}
                arrow
              >
                <Typography
                  variant="body2"
                  align="center"
                  fontWeight={600}
                  sx={{
                    color: item.color,
                    fontSize: "12px",
                    display: "table-caption",
                  }}
                >
                  {item?.date ? formatShortDate(item.date) : "  -"}
                </Typography>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

/* ─── Main list component ─── */
interface StorageLeadListProps {
  setGenerateId: (id: string | null) => void;
}

const StorageLeadList: React.FC<StorageLeadListProps> = ({ setGenerateId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isScreenLarge = useMediaQuery("(max-width:1248px)");
  const { storageList, storagePagination, response } = useSelector(
    (state: any) => state.storage,
  );
  const [selectedLead, setSelectedLead] = useState<string | null>(
    storageList[0]?.leadId || null,
  );

  const [filterOption, setFilterOption] = useState<any>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
    siteType: "",
    storageType: "",
  });
  console.log("storageList", storageList);
  /* ─── ManageStorage popup state ─── */
  const [manageStorageOpen, setManageStorageOpen] = useState(false);
  const [manageStorageTitle, setManageStorageTitle] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  // Initialize with first lead selected
  useEffect(() => {
    if (storageList.length === 0) return;

    const selectedLeadExists = storageList.some(
      (lead: any) => lead.leadId === selectedLead,
    );

    const leadIdToSelect =
      selectedLead && selectedLeadExists
        ? selectedLead
        : storageList[0]?.leadId;

    if (!leadIdToSelect) return;

    if (selectedLead !== leadIdToSelect) {
      setSelectedLead(leadIdToSelect);
    }

    // Parent component ko selected lead ID dobara provide karein
    setGenerateId(leadIdToSelect);
  }, [storageList, selectedLead, setGenerateId]);

  // Search debounce - 500ms
  useEffect(() => {
    if (searchValue === filterOption.search) return;

    const timer = setTimeout(() => {
      setFilterOption((prev: any) => ({
        ...prev,
        page: 1,
        search: searchValue.trim(),
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue, filterOption.search]);

  const handleSelect = (id: string) => {
    setGenerateId(id);
    setSelectedLead(id);
  };

  useEffect(() => {
    if (filterOption) {
      dispatch(getAllStorageAction(filterOption));
    }
  }, [dispatch, filterOption]);

  useEffect(() => {
    if (response?.addStorage || response?.updateStorage) {
      dispatch(setUpdateLeadAction(false));
      dispatch(getAllStorageAction(filterOption));
      if (selectedLead) {
        dispatch(getSingleStorageAction({ storageId: selectedLead }));
      }
      dispatch(setAddNewLeadAction(false));
      dispatch(setStorageResponse(false));
      setManageStorageOpen(false);
    }
  }, [
    dispatch,
    response?.addStorage,
    response?.updateStorage,
    dispatch,
    selectedLead,
  ]);

  const handlePageChange = (newPage: number) => {
    setFilterOption((prev: any) => {
      const updated = { ...prev, page: newPage };
      return updated;
    });
  };

  const handleFilter = () => {
    setOpenFilter(true);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  /**
   * Apply filter from modal
   */
  const handleApplyFilter = (data: any) => {
    setFilterOption((prev: any) => ({
      ...prev,
      page: 1,
      siteType: data.siteType,
      storageType: data.storageType,
    }));
  };

  /**
   * Clear filter
   */
  const handleClearFilter = () => {
    setFilterOption((prev: any) => ({
      ...prev,
      page: 1,
      siteType: "",
      storageType: "",
    }));
  };

  /**
   * Close filter modal
   */
  const handleHideFilter = () => {
    setOpenFilter(false);
  };

  return (
    <>
      {/* Filter Modal */}
      {openFilter && (
        <ManageStorageFilter
          open={openFilter}
          title="Filter Storage"
          onHide={handleHideFilter}
          filterData={{
            siteType: filterOption.siteType,
            storageType: filterOption.storageType,
          }}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        />
      )}
      <Box sx={{ flexDirection: "column" }}>
        {/* ─── ManageStorage Popup ─── */}
        {manageStorageOpen && (
          <ManageStorage
            selectLead={selectedLead}
            open={manageStorageOpen}
            onHide={() => setManageStorageOpen(false)}
            title={manageStorageTitle}
            screen="storage"
          />
        )}

        {/* Top Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "9px",
            background: "#fff",
            border: "1px solid #C4CDD5",
          }}
        >
          <CustomButton
            style={{ fontSize: "12px", padding: "4px 8px" }}
            title="New Storage Lead"
            onClick={() => {
              setManageStorageTitle("Add New Storage");
              setManageStorageOpen(true);
            }}
          />
          {/* <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="Filter"
          variant="outlined"
          onClick={handleFilter}
        /> */}
        </Box>

        {/* List Header */}
        {/* Search Box */}
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 1,
            px: 0.5,
          }}
        >
          <TextField
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search storage leads..."
            sx={{
              width: { xs: "100%", sm: 300 },
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box> */}
        {storageList.length > 0 ? (
          <Box sx={{ width: "100%", paddingBottom: 4, paddingTop: 1 }}>
            <Grid
              container
              spacing={0}
              alignItems="center"
              sx={{
                background: "#E5F4F1",
                color: "#637381",
                padding: "10px 10px",
                marginBottom: "4px",
                border: "1px solid #C4CDD5",
              }}
            >
              <Grid item xs={6} sx={{ borderRight: "1px solid #C4CDD5" }}>
                <Typography variant="body2" fontWeight={600}>
                  Customer List
                </Typography>
              </Grid>
              <Grid
                item
                xs
                sx={{
                  textAlign: "center",
                  borderRight: "1px solid #C4CDD5",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  F
                </Typography>
              </Grid>
              <Grid
                item
                xs
                sx={{
                  textAlign: "center",
                  borderRight: "1px solid #C4CDD5",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  A
                </Typography>
              </Grid>
              <Grid
                item
                xs
                sx={{
                  textAlign: "center",
                  borderRight: "1px solid #C4CDD5",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  M
                </Typography>
              </Grid>
              <Grid item xs sx={{ textAlign: "center" }}>
                <Typography variant="body2" fontWeight={600}>
                  L
                </Typography>
              </Grid>
            </Grid>

            {/* Lead Cards */}
            {storageList.map((lead: any) => (
              <StorageLeadListCard
                key={lead.leadId}
                lead={lead}
                selectedLead={selectedLead}
                onSelect={handleSelect}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              padding: "2px 10px",
              height: "60vh",
              display: "flex",
              textAlign: "center",
              alignItems: "center",
              color: "#4A4A4A",
              justifyContent: "center",
            }}
          >
            No storage leads available
          </Box>
        )}

        {/* Pagination */}
        {storageLeadList.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: 2,
              alignItems: "center",
              borderTop: "2px solid #d5d5d5",
            }}
          >
            <Button
              size="small"
              onClick={() =>
                handlePageChange(storagePagination?.currentPage - 1)
              }
              variant="contained"
              color="secondary"
              sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
              disabled={storagePagination?.currentPage === 1}
            >
              <FirstPage sx={{ fontSize: "16px" }} />
            </Button>
            <Typography
              sx={{
                fontSize:
                  isMdUp && !isScreenLarge
                    ? "13px"
                    : isScreenLarge
                      ? "12px"
                      : "12px",
                textAlign: "center",
              }}
            >
              {storagePagination.totalItems} listings found. Page{" "}
              {storagePagination?.currentPage} of{" "}
              {storagePagination?.totalPages}.
            </Typography>
            <Button
              onClick={() =>
                handlePageChange(storagePagination?.currentPage + 1)
              }
              variant="contained"
              color="secondary"
              sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
              disabled={!storagePagination?.hasNextPage}
            >
              <LastPage sx={{ fontSize: "16px" }} />
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default StorageLeadList;
